import * as moment from "moment";
import { auth, storage, database, regularAuth } from "../firebase";

export const retrieveCompanyList = async (id) => {
  try {
    const request = await fetch(`/database/retrieve-user-companies/${id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const response = await request.json();
    if (response.error === null) {
      return { error: null, list: response.payload };
    } else {
      throw response.error;
    }
  } catch (e) {
    return { error: null, list: [] };
  }
};

export const retrieveInitialNotificationsList = async () => {
  try {
    let notifications = [];

    const allNotifications = await database
      .collection("notifications")
      .where("recipient", "==", auth.currentUser.uid)
      .orderBy("timestamp", "desc")
      .get();
    if (allNotifications.size > 0) {
      await Promise.allSettled(
        allNotifications.docs.map(async (notification) => {
          if (notification.exists) {
            notifications.push({ id: notification.id, ...notification.data() });
          }
        })
      ).catch((e) => {
        throw new Error("Kunne ikke hente varsler.");
      });
      return Promise.all(notifications).then((result) => {
        return {
          error: null,
          payload: { content: result, size: allNotifications.size },
        };
      });
    } else {
      return { error: null, payload: { content: [], size: 0 } };
    }
  } catch (e) {
    throw { error: null, list: [] };
  }
};

export const retrieveUserDetails = async (id) => {
  try {
    const userInfo = await database.collection("users").doc(id).get();
    const contact = await database
      .collection("users")
      .doc(id)
      .collection("details")
      .doc("contact")
      .get();
    const notifications = await retrieveInitialNotificationsList(id);
    const companies = await retrieveCompanyList(id);

    const userObject = {
      id: userInfo.id,
      firstName: userInfo.data().firstName ? userInfo.data().firstName : null,
      lastName: userInfo.data().lastName ? userInfo.data().lastName : null,
      city: contact.exists && contact.data().city ? contact.data().city : null,
      address:
        contact.exists && contact.data().address
          ? contact.data().address
          : null,
      country:
        contact.exists && contact.data().country
          ? contact.data().country
          : null,
      companyId: userInfo.data().companyId ? userInfo.data().companyId : null,
      mobile:
        contact.exists && contact.data().mobile ? contact.data().mobile : null,
      postalCode:
        contact.exists && contact.data().postalCode
          ? contact.data().postalCode
          : null,
      thumbnail: userInfo.data().thumbnail ? userInfo.data().thumbnail : null,
      notifications: (notifications && notifications.payload) || [],
      companies: (companies && companies.list) || [],
    };

    return { payload: userObject, error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const updateUserDetails = async (details) => {
  try {
    if (details.type === "short") {
      const publicDetails = {
        firstName: details.info.firstName,
        lastName: details.info.lastName,
      };
      const contactDetails = { mobile: details.info.mobile };
      await database
        .collection("users")
        .doc(details.user)
        .update(publicDetails);
      const contact = await database
        .collection("users")
        .doc(details.user)
        .collection("details")
        .doc("contact")
        .get();
      if (contact.exists) {
        await database
          .collection("users")
          .doc(details.user)
          .collection("details")
          .doc("contact")
          .update(contactDetails);
      } else {
        await database
          .collection("users")
          .doc(details.user)
          .collection("details")
          .doc("contact")
          .set(contactDetails);
      }
    } else {
      const contactDetails = {
        address: details.info.address,
        postalCode: details.info.postalCode,
        city: details.info.city,
        country: details.info.country,
      };
      const contact = await database
        .collection("users")
        .doc(details.user)
        .collection("details")
        .doc("contact")
        .get();
      if (contact.exists) {
        await database
          .collection("users")
          .doc(details.user)
          .collection("details")
          .doc("contact")
          .update(contactDetails);
      } else {
        await database
          .collection("users")
          .doc(details.user)
          .collection("details")
          .doc("contact")
          .set(contactDetails);
      }
    }
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveUserFullName = async (id) => {
  try {
    const info = await database.collection("users").doc(id).get();
    if (info.exists) {
      return {
        payload: {
          firstName: info.data().firstName,
          lastName: info.data().lastName,
        },
        error: null,
      };
    } else {
      throw "Kunne ikke hente navn.";
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const performPasswordChange = async (details) => {
  try {
    var user = auth.currentUser;
    var credentials = regularAuth.EmailAuthProvider.credential(
      user.email,
      details.existingPassword
    );
    await user.reauthenticateWithCredential(credentials);
    await user.updatePassword(details.newPassword);
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveUserLogins = async (id) => {
  try {
    const allLogins = [];
    const logins = await database
      .collection("users")
      .doc(id)
      .collection("logins")
      .get();
    logins.forEach((login) => {
      allLogins.push({
        id: login.id,
        city: login.data().city,
        country: login.data().country,
        isp: login.data().isp,
        query: login.data().query,
        timestamp: login.data().timestamp,
      });
    });
    return Promise.all(allLogins).then((result) => {
      return { error: null, payload: result };
    });
  } catch (e) {
    throw new Error(e);
  }
};

export const updateUserImage = async (details) => {
  try {
    if (details.image) {
      const storageRef = storage.ref();
      const uploadRef = storageRef.child(
        "hosting/user/" + auth.currentUser.uid + "/thumbnail"
      );
      const upload = await uploadRef.put(details.image);

      const url = await upload.ref.getDownloadURL();
      await database
        .collection("users")
        .doc(auth.currentUser.uid)
        .update({ thumbnail: url });
      return { error: null };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const deleteUserImage = async (id) => {
  try {
    const storageRef = storage.ref();
    const uploadRef = storageRef.child("hosting/user/" + id + "/thumbnail");
    await uploadRef.delete();
    await database.collection("users").doc(id).update({ thumbnail: null });
    return { error: null };
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveUserImage = async (id) => {
  try {
    const response = await database.collection("users").doc(id).get();
    if (response.exists) {
      return { payload: response.data().thumbnail, error: null };
    } else {
      return { payload: null, error: "Ingen bilde." };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveNotifications = async (details) => {
  try {
    //const additions = []
    let notifications = {
      showing: [],
      size: 0,
    };
    if (details && details.current && details.current.length > 0) {
      const lastNotification = details.current[0].timestamp;
      const allNotifications = await database
        .collection("notifications")
        .where("recipient", "==", auth.currentUser.uid)
        .orderBy("timestamp")
        .startAt(lastNotification)
        .limitToLast(5)
        .get();
      notifications.size = allNotifications.size;
      allNotifications.forEach((notification) => {
        notifications.showing.push({
          id: notification.id,
          seen: notification.data().seen,
          accepted: notification.data().accepted,
          companyId: notification.data().companyId,
          companyName: notification.data().companyName,
          expires: notification.data().expires,
          type: notification.data().type,
          timestamp: notification.data().timestamp,
        });
      });
      return { error: null, payload: notifications };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const authenticateUser = async (user) => {
  try {
    const login = await auth.signInWithEmailAndPassword(
      user.email,
      user.password
    );
    await fetchDetails(login.user.uid);
    await fetchGeolocation(login.user.uid);

    return { error: null, payload: "success" };
  } catch (e) {
    throw new Error(e);
  }
};

export const fetchGeolocation = async (id) => {
  try {
    const response = await fetch(
      `https://pro.ip-api.com/json/?key=${process.env.REACT_APP_LOCATION_API_KEY}`
    );
    const data = await response.json();
    const login = {
      as: data.as,
      city: data.city,
      country: data.country,
      countryCode: data.countryCode,
      isp: data.isp,
      lat: data.lat,
      lon: data.lon,
      query: data.query,
      timezone: data.timezone,
      zip: data.zip,
      timestamp: moment().unix(),
    };
    await database.collection("users").doc(id).collection("logins").add(login);
    return { error: null, payload: "success" };
  } catch (e) {
    throw new Error(e);
  }
};

export const fetchDetails = async (uid) => {
  return await database.collection("users").doc(uid).get();
};

export const acceptInvitation = async (details) => {
  try {
    const request = await fetch("/database/accept-invitation", {
      method: "POST",
      body: JSON.stringify(details),
      headers: { "Content-Type": "application/json" },
    });
    const response = await request.json();
    if (response.error === null) {
      return { error: null };
    } else {
      return { error: "Kunne ikke akseptere invitasjon." };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const rejectInvitation = async (details) => {
  try {
    const request = await fetch("/database/reject-invitation", {
      method: "POST",
      body: JSON.stringify(details),
      headers: { "Content-Type": "application/json" },
    });
    const response = await request.json();
    if (response.error === null) {
      return { error: null };
    } else {
      return { error: "Kunne ikke avvise invitasjon." };
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const updateUnseenNotifications = async (details) => {
  try {
    const request = await fetch("/database/update-unseen-notifications", {
      method: "POST",
      body: JSON.stringify(details),
      headers: { "Content-Type": "application/json" },
    });
    const response = await request.json();
    if (response.error === null) {
      return { error: null };
    } else {
      //Should it even trigger an error on this event?
    }
  } catch (e) {
    throw new Error(e);
  }
};

export const retrieveNotificationsList = async (details) => {
  try {
    let notifications = [];

    const allNotifications = await database
      .collection("notifications")
      .where("recipient", "==", auth.currentUser.uid)
      .orderBy("timestamp", "desc")
      .get();
    if (allNotifications.size > 0) {
      await Promise.allSettled(
        allNotifications.docs.map(async (notification) => {
          if (notification.exists) {
            notifications.push({ id: notification.id, ...notification.data() });
          }
        })
      ).catch((e) => {
        throw new Error("Kunne ikke hente varsler.");
      });

      return Promise.all(notifications).then((result) => {
        return {
          error: null,
          payload: { content: result, size: allNotifications.size },
        };
      });
    } else {
      return { error: null, payload: { content: [], size: 0 } };
    }
  } catch (e) {
    throw new Error(e);
  }
};
